import Guide from './Guide';
import Overlay from './Overlay';
import Container from './Container';
import ChecklistContainer from './ChecklistContainer';
import HotspotContainer from './HotspotContainer';
import HotspotLayer from './HotspotLayer';
import BannerContainer from './BannerContainer';
import Survey from './Survey';
import Iframe from './Iframe';
import Popup from './Popup';
import Checklist from './Checklist';
import { v4 } from 'uuid';
import { MessageTypes } from '../types/MessageTypes';
import { printGuideLog } from '../helpers/guideLogHelper';
import { isGuideDisabled } from '../helpers/guideDisableHelper';
import { hasVisiblePasswordField } from '../helpers/installSignalHelper';

class GuideManager {
  private static _instance: GuideManager;
  private guide: Guide | null = null;
  private survey: Survey | null = null;
  private checklist: Checklist | null = null;
  private popup: Popup | null = null;

  private lastIdentifiedUser: unknown = null;
  /**
   * O widget já se registrou sozinho como anônimo nesta página (a fila chegou sem `identify`). Um
   * `Getdemo.identify(id)` depois disso é o identify tardio que o diagnóstico de instalação aponta.
   */
  private autoIdentifiedAnonymously = false;

  public static getInstance(): GuideManager {
    if (!GuideManager._instance) {
      GuideManager._instance = new GuideManager();
    }
    return GuideManager._instance;
  }

  public async start() {
    if (isGuideDisabled()) {
      this.applyDisabledStubApi();
      return;
    }
    if (window.top !== window.self) {
      this.startIframe();
    } else {
      await this.startMain();
    }
  }

  private applyDisabledStubApi() {
    if (!window.Getdemo) {
      window.Getdemo = { q: [] };
    }
    if (window.Getdemo.q) {
      window.Getdemo.q = [];
    }
    window.Getdemo.identify = async () => {};
    window.Getdemo.trigger = async () => {};
  }

  private async startMain() {
    this.startListeners();
    Container.getInstance();
    ChecklistContainer.getInstance();
    HotspotContainer.getInstance();
    BannerContainer.getInstance();

    await this.waitInitialized();
    this.injectApi();
    await this.processQueue();
    this.checkForAutoTrigger();
  }

  private checkForAutoTrigger() {
    const urlParams = new URLSearchParams(window.location.search);
    const guideId = urlParams.get('guideId');
    if (guideId) {
      printGuideLog({
        message: '[GuideManager] Auto trigger via query string detectado',
        data: { guideId }
      });
      setTimeout(() => {
        this.triggerItem(guideId);
      }, 1000);
    }
  }

  private async startIframe() {
    const hasScript = await new Promise(resolve => {
      window.parent.postMessage(
        {
          type: MessageTypes.CHECK_IF_PARENT_HAS_SCRIPT
        },
        '*'
      );
      window.addEventListener('message', event => {
        if (event.data.type === MessageTypes.PARENT_HAS_SCRIPT) {
          resolve(true);
        }
      });
      setTimeout(() => {
        resolve(false);
      }, 1000);
    });
    if (!hasScript) {
      await this.startMain();
    } else {
      this.startIframeListeners();
    }
  }

  private startIframeListeners() {
    window.addEventListener('message', event => {
      if (event.data.type === MessageTypes.CHECK_IF_PARENT_HAS_SCRIPT) {
        event.source?.postMessage({
          type: MessageTypes.PARENT_HAS_SCRIPT
        });
      }
    });
    Iframe.getInstance();
  }

  /**
   * Every player iframe that filters content by URL has to hear about a
   * navigation, and forgetting one of them is a silent bug (the page changes
   * and that surface keeps showing the old target's content). One broadcaster
   * so adding a surface is a one-line change here.
   */
  private static broadcastCurrentUrl(url: string = window.location.href) {
    [Container, ChecklistContainer, HotspotContainer, BannerContainer].forEach(surface => {
      surface.getInstance().sendMessage({
        type: MessageTypes.SET_CURRENT_URL,
        url
      });
    });
  }

  private async waitInitialized() {
    const checklistPromise = new Promise(resolve => {
      window.addEventListener('message', event => {
        if (event.data.type === MessageTypes.INITIALIZED_CHECKLIST) {
          ChecklistContainer.getInstance().sendMessage({
            type: MessageTypes.SET_CURRENT_URL,
            url: window.location.href
          });
          resolve(true);
        }
      });
    });
    const guidePromise = new Promise(resolve => {
      window.addEventListener('message', event => {
        if (event.data.type === MessageTypes.INITIALIZED_GUIDE) {
          Container.getInstance().sendMessage({
            type: MessageTypes.SET_CURRENT_URL,
            url: window.location.href
          });
          resolve(true);
        }
      });
    });
    // Hotspots and banners are deliberately absent from this gate: they are
    // passive, so a slow iframe must never delay the guide/checklist boot.
    window.addEventListener('message', event => {
      if (event.data.type === MessageTypes.INITIALIZED_HOTSPOTS) {
        HotspotContainer.getInstance().sendMessage({
          type: MessageTypes.SET_CURRENT_URL,
          url: window.location.href
        });
        this.deliverPassiveIdentify(undefined, [HotspotContainer]);
      } else if (event.data.type === MessageTypes.INITIALIZED_BANNERS) {
        BannerContainer.getInstance().sendMessage({
          type: MessageTypes.SET_CURRENT_URL,
          url: window.location.href
        });
        this.deliverPassiveIdentify(undefined, [BannerContainer]);
      }
    });
    return Promise.all([checklistPromise, guidePromise]);
  }

  private startListeners() {
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.data.type === MessageTypes.GET_CURRENT_URL) {
        printGuideLog({
          message: '[GuideManager] Mensagem GET_CURRENT_URL recebida',
          data: { currentUrl: window.location.href }
        });
        GuideManager.broadcastCurrentUrl();
      } else if (event.data.type === MessageTypes.SET_HOTSPOT_TARGETS) {
        HotspotLayer.getInstance().setTargets(event.data.targets || []);
      } else if (event.data.type === MessageTypes.HOTSPOT_DISMISSED) {
        HotspotLayer.getInstance().dismiss(event.data.pointId);
      } else if (event.data.type === MessageTypes.RESIZE_AND_POSITION_HOTSPOT_IFRAME) {
        HotspotLayer.getInstance().resizeTooltip(event.data);
      } else if (event.data.type === MessageTypes.RESIZE_AND_POSITION_BANNER_IFRAME) {
        const banner = BannerContainer.getInstance();
        if (event.data.show) {
          banner.layout({
            placement: event.data.placement === 'BOTTOM' ? 'BOTTOM' : 'TOP',
            dimensions: event.data.dimensions
          });
          banner.show();
        } else {
          banner.hide();
        }
      } else if (event.data.type === MessageTypes.RUN_GUIDE) {
        printGuideLog({ message: '[GuideManager] Mensagem RUN_GUIDE recebida' });
        this.guide?.destroy();
        this.survey?.destroy();
        this.popup?.destroy();

        this.guide = new Guide();
      } else if (event.data.type === MessageTypes.RUN_SURVEY) {
        printGuideLog({ message: '[GuideManager] Mensagem RUN_SURVEY recebida' });
        this.guide?.destroy();
        this.survey?.destroy();
        this.popup?.destroy();

        this.survey = new Survey();
      } else if (event.data.type === MessageTypes.RUN_POPUP) {
        printGuideLog({ message: '[GuideManager] Mensagem RUN_POPUP recebida' });
        this.survey?.destroy();
        this.guide?.destroy();
        this.popup?.destroy();

        this.popup = new Popup();
      } else if (event.data.type === MessageTypes.RUN_CHECKLIST) {
        printGuideLog({ message: '[GuideManager] Mensagem RUN_CHECKLIST recebida' });
        this.checklist = new Checklist();
      } else if (event.data.type === MessageTypes.DESTROY) {
        this.destroy();
      } else if (event.data.type === MessageTypes.CHECK_IF_PARENT_HAS_SCRIPT) {
        event.source?.postMessage(
          {
            type: MessageTypes.PARENT_HAS_SCRIPT
          },
          { targetOrigin: event.origin }
        );
      } else if (event.data.type === MessageTypes.GUIDE_COMPLETED) {
        printGuideLog({
          message: '[GuideManager] Mensagem GUIDE_COMPLETED recebida',
          data: { guideId: event.data.guideId }
        });
        ChecklistContainer.getInstance().sendMessage({
          type: MessageTypes.GUIDE_COMPLETED,
          guideId: event.data.guideId
        });
      } else if (event.data.type === MessageTypes.OPEN_LINK) {
        printGuideLog({
          message: '[GuideManager] Mensagem OPEN_LINK recebida',
          data: { href: event.data.href }
        });
        window.open(event.data.href, '_blank');
      } else if (event.data.type === MessageTypes.TRIGGER_ITEM) {
        printGuideLog({
          message: '[GuideManager] Mensagem TRIGGER_ITEM recebida',
          data: { id: event.data.id, redirectUrl: event.data.redirectUrl }
        });
        this.triggerItem(event.data.id, event.data.redirectUrl);
      } else if (event.data.type === MessageTypes.REDIRECT) {
        printGuideLog({
          message: '[GuideManager] Mensagem REDIRECT recebida',
          data: { redirectUrl: event.data.redirectUrl }
        });
        window.location.href = event.data.redirectUrl;
      } else if (event.data.type === 'REDIRECT_TO_GUIDE') {
        const { url, guideId } = event.data;

        const normalizeUrlForComparison = (urlStr: string) => {
          try {
            const urlObj = new URL(urlStr);
            return `${urlObj.origin}${urlObj.pathname}`.replace(/\/$/, '');
          } catch {
            return urlStr.split('?')[0].split('#')[0].replace(/\/$/, '');
          }
        };

        const currentUrl = normalizeUrlForComparison(window.location.href);
        const targetUrl = normalizeUrlForComparison(url);

        if (currentUrl === targetUrl) {
          printGuideLog({
            message: '[GuideManager] REDIRECT_TO_GUIDE na mesma URL, disparando trigger',
            data: { guideId }
          });
          // Same-page guides fire in place (no navigation), so the gdToken carried in `url` would be
          // lost — and the guide-progress calls read the token from the page URL. Push it into the
          // current URL first so the run correlates to the bot lead (timeline/observação).
          try {
            const incoming = new URL(url, window.location.href);
            const gdToken = incoming.searchParams.get('gdToken');
            if (gdToken) {
              const currentWithToken = new URL(window.location.href);
              currentWithToken.searchParams.set('gdToken', gdToken);
              window.history.replaceState(null, '', currentWithToken.toString());
            }
          } catch {
            // ignore URL parsing failures; trigger anyway
          }
          if (window.Getdemo && window.Getdemo.trigger) {
            window.Getdemo.trigger(guideId);
          }
        } else {
          printGuideLog({
            message: '[GuideManager] REDIRECT_TO_GUIDE para outra URL, redirecionando',
            data: { from: currentUrl, to: url, guideId }
          });
          window.location.href = url;
        }
      }
    });

    this.startUrlChangeListener();
  }

  private destroy = () => {
    this.guide?.destroy();
    this.guide = null;
    this.survey?.destroy();
    this.survey = null;
    this.popup?.destroy();
    this.popup = null;
    Container.getInstance().hide();
    Overlay.getInstance().destroy();
  };

  private startUrlChangeListener() {
    let currentUrl = window.location.href;

    window.addEventListener('popstate', () => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;

        GuideManager.broadcastCurrentUrl(currentUrl);
      }
    });

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;

        GuideManager.broadcastCurrentUrl(currentUrl);
      }
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;

        GuideManager.broadcastCurrentUrl(currentUrl);
      }
    };
  }

  private injectApi() {
    if (!window.Getdemo) {
      window.Getdemo = { q: [] };

      window.Getdemo.c = function (n: string) {
        return function (...args: any[]) {
          (window.Getdemo.q as Array<[string, any[]]>).push([n, args]);
        };
      };

      const methods = ['identify', 'trigger', 'instruction'];
      for (let j = 0; j < methods.length; j += 1) {
        window.Getdemo[methods[j]] = window.Getdemo.c(methods[j]);
      }
    }
  }

  /** Hotspots and banners never call identify themselves; they only receive the result. */
  private deliverPassiveIdentify = (
    user?: unknown,
    surfaces: Array<{ getInstance(): { sendMessage(message: unknown): void } }> = [
      HotspotContainer,
      BannerContainer
    ]
  ) => {
    const identifiedUser =
      user ??
      this.lastIdentifiedUser ??
      (() => {
        const savedUserData = localStorage.getItem('getdemo_user');
        if (!savedUserData) return null;
        try {
          return JSON.parse(savedUserData);
        } catch {
          return null;
        }
      })();

    if (!identifiedUser) return;

    this.lastIdentifiedUser = identifiedUser;
    surfaces.forEach(surface =>
      surface.getInstance().sendMessage({
        type: MessageTypes.IDENTIFY_USER,
        user: identifiedUser
      })
    );
  };

  private identifyUser = async (id?: string, params?: Record<string, any>) => {
    const lateIdentify = this.autoIdentifiedAnonymously && !!id;
    return new Promise(resolve => {
      const runId = v4();
      Container.getInstance().sendMessage({
        type: MessageTypes.IDENTIFY_USER,
        id,
        params,
        runId,
        pageUrl: window.location.href,
        loginPage: !id && hasVisiblePasswordField(),
        lateIdentify
      });
      const handler = (event: MessageEvent) => {
        if (event.data.type === MessageTypes.IDENTIFY_USER_SUCCESS) {
          if (event.data.runId === runId) {
            ChecklistContainer.getInstance().sendMessage({
              type: MessageTypes.IDENTIFY_USER,
              user: event.data.user
            });
            this.deliverPassiveIdentify(event.data.user);
            window.removeEventListener('message', handler);
            resolve(true);
          }
        }
      };
      window.addEventListener('message', handler);
    });
  };

  private processQueue = async () => {
    window.Getdemo.identify = this.identifyUser;
    window.Getdemo.trigger = this.triggerItem;

    let hasIdentify = false;

    if (window.Getdemo.q) {
      const queued = window.Getdemo.q as Array<[string, any[]]>;
      // Devolve à fila o que NÃO é deste dono. Zerar `q` inteiro descartava, em silêncio, qualquer
      // método drenado por outro consumidor — hoje `['instruction', …]` (lido pelo embed, em
      // `features/embed/instruction.ts`) e `['on', …]` (lido por `ensureOnSurface`). Um guia na
      // página fazia a chamada do cliente sumir sem erro nenhum, dependendo da ordem de boot.
      window.Getdemo.q = queued.filter(([method]) => method !== 'identify' && method !== 'trigger');
      for (let i = 0; i < queued.length; i += 1) {
        const [method, args] = queued[i];
        if (method === 'identify') {
          hasIdentify = true;
          await this.identifyUser(args[0], args[1]);
        }
      }
      for (let i = 0; i < queued.length; i += 1) {
        const [method, args] = queued[i];
        if (method === 'trigger') {
          await this.triggerItem(args[0]);
        }
      }
    }

    if (!hasIdentify) {
      this.autoIdentifiedAnonymously = true;
      await this.identifyUser();
    }
  };

  private triggerItem = async (id: string, redirectUrl?: string) => {
    printGuideLog({
      message: '[GuideManager] triggerItem chamado',
      data: { id, redirectUrl }
    });
    Container.getInstance().sendMessage({
      type: MessageTypes.TRIGGER_ITEM,
      id,
      redirectUrl
    });
    // `Getdemo.trigger(id)` is resource-agnostic; the banner and checklist players ignore ids
    // that are not their own.
    BannerContainer.getInstance().sendMessage({ type: MessageTypes.TRIGGER_ITEM, id });
    ChecklistContainer.getInstance().sendMessage({ type: MessageTypes.TRIGGER_ITEM, id });
  };
}

export default GuideManager;
